import {useQuery} from '@apollo/client';
import {COUNT_INSTITUTIONAL_ACCOUNTS} from './resolvers';
import {buildInstitutionalAccountFilter} from './kybUtils';

/**
 * Badge numbers for the queue tabs.
 *
 * The API exposes no per-status counts, so this is one `limit: 1` query per
 * status, read for `pagination.total`. It is deliberately isolated: when the
 * filter grows counts of its own, deleting this hook and its call site is the
 * whole change.
 *
 * The counts respect the other active filters, so a tab does not claim twelve
 * submitted accounts while a search is narrowing the table to one. Hooks
 * cannot be called in a loop, so each status is spelled out.
 */
const useCount = (status, filters) => {
  const {data} = useQuery(COUNT_INSTITUTIONAL_ACCOUNTS, {
    variables: {
      filter: buildInstitutionalAccountFilter({
        ...filters,
        kybStatus: status,
        page: 1,
        limit: 1,
      }),
    },
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
  });

  const payload = data?.getInstitutionalAccounts;
  // A null count renders as no badge at all, which is honest: an authorization
  // failure or a network blip must not be shown as "0 accounts".
  if (!payload || payload.__typename === 'Error') return null;
  return payload?.pagination?.total ?? null;
};

export const useKybStatusCounts = (filters = {}) => ({
  SUBMITTED: useCount('SUBMITTED', filters),
  UNDER_REVIEW: useCount('UNDER_REVIEW', filters),
  MORE_INFO: useCount('MORE_INFO', filters),
  APPROVED: useCount('APPROVED', filters),
  REJECTED: useCount('REJECTED', filters),
  DRAFT: useCount('DRAFT', filters),
});
